import { useMutation } from "@tanstack/react-query";
import { HTTPError } from "ky";

import { CONSTANTS } from "@/config/constants";
import { isMockEnabled, simulateDelay } from "@/mock/config";
import { createOrganizationMock } from "@/mock/handlers/organization";
import {
  CreateOrganizationPayload,
  CreateOrganizationResponse,
} from "@/types/organization";

import { api } from "@/lib/api-client";

export const useCreateOrganization = () => {
  return useMutation({
    mutationFn: async (variables: CreateOrganizationPayload) => {
      if (isMockEnabled()) {
        await simulateDelay();
        return createOrganizationMock(variables);
      }

      try {
        const data = await api
          .post("organizations", { json: variables })
          .json<CreateOrganizationResponse>();

        return data;
      } catch (error) {
        if (error instanceof HTTPError && error.response.status === 409) {
          throw new Error(
            "An organization with this name already exists. Please choose a different name.",
          );
        }

        if (error instanceof HTTPError) {
          throw new Error(
            `Oops! We ran into an issue while creating your organization. Please try again shortly. If this keeps happening, reach out to us at ${CONSTANTS.SUPPORT_EMAIL}. We're happy to help.`,
          );
        }

        throw error;
      }
    },
    retry: 2,
  });
};
